/**
 * A labelled form field, in the template's own markup.
 *
 * <p>
 * <strong>This exists because there were four of it.</strong> `AvailabilityPage`,
 * `NewBookingPage`, `StockIntakePage` and `GarmentStateDrawer` each declared a private `Field`, and
 * the four disagreed: 12px against the template's 14px, `font-medium` against `font-semibold`,
 * `text-gray-900` (#2f3a4b) against `text-default` (#67707d) against the template's `text-dark`
 * (#1e293b). None of them matched what an Ant Design `Form.Item` rendered two screens away. A label
 * is the most repeated element in an ERP and the one nobody looks at twice, which is exactly how
 * four spellings of it survived.
 * </p>
 *
 * <p>
 * <strong>The values are the template's offcanvas convention, not its component-demo page.</strong>
 * The template writes a field two ways: `formElements` — its showroom for input variants — uses
 * `font-medium text-dark mb-2`, while every real form panel in it (`crm/contacts`, `crm/deals`, the
 * rest) uses `text-sm font-semibold text-gray-900 mb-1` with a `text-danger` asterisk. Every form in
 * this product is a drawer, so the panels are what it follows. `style.css` points Ant Design's
 * labels at the same values, which is the load-bearing half: the two ways of writing a field sit
 * side by side inside one drawer.
 * </p>
 *
 * <p>
 * A `<label>` rather than a `<div>` with an id, so clicking the text focuses the control without
 * every caller inventing an id. The hint is inside it too, which is what a screen reader reads out
 * with the field rather than after it.
 * </p>
 */
export function Field({
  label,
  hint,
  required = false,
  className = '',
  children,
}: {
  label: string;
  /** The sentence under the control — a rule, a unit, what the value means. */
  hint?: string | undefined;
  /**
   * Draws the template's danger asterisk after the label. Presentational only: what is actually
   * required is decided by the form that submits, never by this.
   */
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-sm font-semibold text-gray-900">
        {label}
        {required && <span className="text-danger ms-0.5">*</span>}
      </span>
      {children}
      {hint && (
        <span className="text-default mt-1 block text-[length:var(--scale-field-hint)]">
          {hint}
        </span>
      )}
    </label>
  );
}

/**
 * The template's own control, for the inputs Ant Design is not rendering.
 *
 * <p>
 * `w-full px-3 py-2 text-sm border border-border-color rounded-md bg-white`, verbatim from the
 * template's offcanvas forms, plus the disabled state they never show. Not `.form-input`: that class
 * belongs to the `formElements` showroom, where it pins a 37.6px height for a page of side-by-side
 * variants. A drawer's fields want the padding, not the cap.
 * </p>
 *
 * <p>
 * Exported as a string rather than wrapped in a component because these are native `<input>`,
 * `<select>` and `<textarea>` elements with their own attributes — a wrapper would have to forward
 * `type`, `min`, `step`, `rows`, `capture` and the rest, and the first field it forgot would be
 * written by hand and look different again.
 * </p>
 */
export const controlClass =
  'w-full rounded-md border border-border-color bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-light disabled:text-default';

/**
 * The same, for a <select>.
 *
 * <p>
 * <strong>A select styled with `controlClass` is a text box.</strong> That is what it looked like
 * until this existed: identical border, height and padding, with only the small arrow the browser
 * draws in the corner to say it opens — and on Windows that arrow is grey, 8px, and easy to miss on
 * a screen full of white boxes. A clerk cannot tell what is typeable from what is choosable.
 * </p>
 *
 * <p>
 * So: `appearance-none` to drop the OS arrow, and `.select-control` in `style.css` to draw one at
 * a size and weight that reads. The right padding is what stops a long option running under it — a
 * garment called "Navy Three-Piece Tuxedo · Size 40" is the ordinary length here, not the exception.
 * </p>
 *
 * <p>
 * <strong>Still a native select underneath, and that is the point.</strong> On the counter tablet
 * the OS opens its own picker — full-height, thumb-sized rows, one hand — which no custom dropdown
 * matches. Keyboard support, type-ahead and screen-reader behaviour all come free and correct. What
 * it cannot do is search or draw a second line under an option; a list that needs either wants a
 * different control, not a worse select.
 * </p>
 */
export const selectClass = `${controlClass} select-control cursor-pointer appearance-none pr-9`;

/** The same, for a textarea — which grows by rows rather than sitting at one control height. */
export const textAreaClass = `${controlClass} leading-relaxed`;
